import { Directive, ElementRef, HostBinding, HostListener, inject } from '@angular/core';

import {
  A11yBooleanInput,
  A11yExpandable,
  A11yHasPopupValue,
  A11yRole
} from '../interfaces/a11y.interfaces';
import { A11yKeyboardService } from '../services/a11y-keyboard.service';
import {
  isNativeFocusableControl,
  normalizeAriaValue,
  normalizeToken,
  toRequiredAriaBoolean
} from '../utils/a11y.util';

import { A11yLabelDirective } from './a11y-label.directive';

const A11Y_HAS_POPUP_VALUES = ['true', 'false', 'menu', 'listbox', 'tree', 'grid', 'dialog'] as const;

@Directive()
export abstract class A11yExpandableDirective extends A11yLabelDirective implements A11yRole, A11yExpandable {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly keyboardService = inject(A11yKeyboardService);

  protected abstract get expandedInput(): A11yBooleanInput;
  protected abstract get controlsInput(): string | null;

  protected get hasPopupInput(): A11yHasPopupValue {
    return null;
  }

  protected get hasPopupDefault(): A11yHasPopupValue {
    return null;
  }

  @HostBinding('attr.role')
  public get role(): 'button' | null {
    return this.isNativeControl ? null : 'button';
  }

  @HostBinding('attr.tabindex')
  public get tabindex(): string | null {
    return this.isNativeControl ? null : '0';
  }

  @HostBinding('attr.aria-expanded')
  public get ariaExpanded(): 'true' | 'false' {
    return toRequiredAriaBoolean(this.expandedInput);
  }

  @HostBinding('attr.aria-controls')
  public get ariaControls(): string | null {
    return normalizeAriaValue(this.controlsInput);
  }

  @HostBinding('attr.aria-haspopup')
  public get ariaHasPopup(): A11yHasPopupValue {
    return normalizeToken(this.hasPopupInput, A11Y_HAS_POPUP_VALUES, this.hasPopupDefault);
  }

  @HostListener('keydown', ['$event'])
  public onA11yKeydown(event: KeyboardEvent): void {
    if (this.isNativeControl) {
      return;
    }

    this.keyboardService.activateOnEnterOrSpace(event, () => {
      (event.currentTarget as HTMLElement).click();
    });
  }

  protected get isNativeControl(): boolean {
    return isNativeFocusableControl(this.elementRef.nativeElement);
  }
}
